import {
  ArrowLeftOutlined,
  MailOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
  Typography,
} from 'antd';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DeploymentAccessBadge } from '../components/DeploymentAccessBadge';
import { AuthBrand, usePublicBranding } from '../components/AuthBrand';

interface ForgotPasswordForm {
  username: string;
}

export function ForgotPasswordPage() {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string>();
  const [form] = Form.useForm<ForgotPasswordForm>();
  const { siteName, logoUrl } = usePublicBranding();

  async function submit(values: ForgotPasswordForm) {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/auth/password-reset/request', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          username: values.username.trim(),
        }),
      });
      if (!response.ok) {
        setError(
          response.status === 429
            ? 'forgotPassword.rateLimited'
            : 'forgotPassword.unavailable',
        );
        setSubmitted(false);
        return;
      }
      setError(undefined);
      setSubmitted(true);
    } catch {
      setError('forgotPassword.unavailable');
      setSubmitted(false);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-page">
      <header className="login-header">
        <AuthBrand siteName={siteName} logoUrl={logoUrl} />
        <Select
          aria-label={t('common.language')}
          value={i18n.resolvedLanguage ?? 'zh-CN'}
          onChange={(value) => void i18n.changeLanguage(value)}
          options={[
            { value: 'zh-CN', label: '简体中文' },
            { value: 'en-US', label: 'English' },
          ]}
        />
      </header>
      <section className="login-stage" aria-labelledby="forgot-password-title">
        <div className="login-intro">
          <span className="eyebrow">ACCOUNT RECOVERY</span>
          <Typography.Title id="forgot-password-title" level={1}>
            {t('forgotPassword.title')}
          </Typography.Title>
          <Typography.Paragraph>
            {t('forgotPassword.subtitle')}
          </Typography.Paragraph>
        </div>
        <Card className="login-card" variant="borderless">
          {submitted ? (
            <div className="auth-success">
              <Alert
                type="success"
                showIcon
                message={t('forgotPassword.sentTitle')}
                description={t('forgotPassword.sentDescription')}
              />
              <Link to="/login">{t('forgotPassword.backToLogin')}</Link>
            </div>
          ) : (
            <Form<ForgotPasswordForm>
              form={form}
              layout="vertical"
              size="large"
              onFinish={(values) => void submit(values)}
            >
              {error ? (
                <Alert
                  className="auth-form-error"
                  type="error"
                  showIcon
                  message={t(error)}
                />
              ) : null}
              <Form.Item
                label={t('login.username')}
                name="username"
                rules={[
                  {
                    required: true,
                    message: t('forgotPassword.usernameRequired'),
                  },
                ]}
              >
                <Input prefix={<MailOutlined />} autoComplete="username" />
              </Form.Item>
              <Button type="primary" htmlType="submit" block loading={loading}>
                {t('forgotPassword.submit')}
              </Button>
              <div className="auth-back-link">
                <Link to="/login">
                  <ArrowLeftOutlined /> {t('forgotPassword.backToLogin')}
                </Link>
              </div>
            </Form>
          )}
        </Card>
      </section>
      <footer className="login-footer">
        <Space split={<span>·</span>}>
          <DeploymentAccessBadge />
          <span>{t('login.version')}</span>
        </Space>
      </footer>
    </main>
  );
}
